import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { DataGrid, GridToolbarColumnsButton, GridToolbarFilterButton, useGridApiRef } from "@mui/x-data-grid";
import {
	Button,
	Stack,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Snackbar,
	Alert,
	IconButton,
	Tooltip,
	Box,
	Divider,
	Typography,
	Switch,
	TextField,
	Popover,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import axios from "axios";
import * as XLSX from "xlsx";
import PropertyForm from "./PropertyForm";
import dayjs from "dayjs";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import "../datagrid-overrides.css";

const syndeseisOptions = ["Ρεύμα", "Νερό", "Τηλέφωνο", "Ιντερνετ"];

// Custom Date Picker Component
function CustomDatePicker({ value, onChange, placeholder }) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [displayValue, setDisplayValue] = useState("");
	const [selectedDate, setSelectedDate] = useState(null);

	// Convert ISO to DD/MM/YYYY for display
	useEffect(() => {
		if (value) {
			try {
				const date = dayjs(value);
				if (date.isValid()) {
					setDisplayValue(date.format("DD/MM/YYYY"));
					setSelectedDate(date.toDate());
				} else {
					setDisplayValue("");
					setSelectedDate(null);
				}
			} catch (error) {
				setDisplayValue("");
				setSelectedDate(null);
			}
		} else {
			setDisplayValue("");
			setSelectedDate(null);
		}
	}, [value]);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleDateSelect = (date) => {
		if (date) {
			// Use local timezone instead of UTC to avoid date shifting
			const isoValue = dayjs(date).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
			onChange(isoValue);
		}
		handleClose();
	};

	const open = Boolean(anchorEl);

	// Generate calendar days
	const generateCalendarDays = () => {
		const today = selectedDate ? dayjs(selectedDate) : dayjs();
		const startOfMonth = today.startOf("month");
		const endOfMonth = today.endOf("month");
		const startDate = startOfMonth.startOf("week");
		const endDate = endOfMonth.endOf("week");

		const days = [];
		let currentDate = startDate;

		while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, "day")) {
			days.push(currentDate);
			currentDate = currentDate.add(1, "day");
		}

		return days;
	};

	const calendarDays = generateCalendarDays();
	const currentMonth = selectedDate ? dayjs(selectedDate) : dayjs();

	return (
		<Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
			<TextField
				value={displayValue}
				placeholder={placeholder}
				InputProps={{
					readOnly: true,
					style: { fontSize: "14px", padding: "4px 8px" },
				}}
				sx={{
					"& .MuiOutlinedInput-root": {
						border: "none",
						"& fieldset": { border: "none" },
						"&:hover fieldset": { border: "none" },
						"&.Mui-focused fieldset": { border: "none" },
					},
					"& .MuiInputBase-input": {
						padding: "4px 8px",
						fontSize: "14px",
					},
				}}
			/>
			<IconButton size="small" onClick={handleClick} sx={{ padding: "2px", marginLeft: "4px" }}>
				<CalendarTodayIcon fontSize="small" />
			</IconButton>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
			>
				<Box sx={{ p: 2, minWidth: 280 }}>
					<Box sx={{ mb: 2, textAlign: "center" }}>
						<Typography variant="h6">{currentMonth.format("MMMM YYYY")}</Typography>
					</Box>

					{/* Day headers */}
					<Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, mb: 1 }}>
						{["Κυρ", "Δευ", "Τρι", "Τετ", "Πεμ", "Παρ", "Σαβ"].map((day) => (
							<Box key={day} sx={{ textAlign: "center", fontWeight: "bold", fontSize: "12px" }}>
								{day}
							</Box>
						))}
					</Box>

					{/* Calendar grid */}
					<Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
						{calendarDays.map((date, index) => {
							const isCurrentMonth = date.month() === currentMonth.month();
							const isSelected = selectedDate && date.isSame(selectedDate, "day");
							const isToday = date.isSame(dayjs(), "day");

							return (
								<Button
									key={index}
									variant={isSelected ? "contained" : "text"}
									size="small"
									onClick={() => handleDateSelect(date.toDate())}
									sx={{
										minWidth: "32px",
										height: "32px",
										fontSize: "12px",
										color: isCurrentMonth ? "inherit" : "#ccc",
										backgroundColor: isSelected ? "primary.main" : "transparent",
										"&:hover": {
											backgroundColor: isSelected ? "primary.dark" : "action.hover",
										},
										border: isToday ? "2px solid #1976d2" : "none",
									}}
								>
									{date.format("D")}
								</Button>
							);
						})}
					</Box>
				</Box>
			</Popover>
		</Box>
	);
}

const localeText = {
	// Toolbar
	toolbarColumns: "Στήλες",
	toolbarFilters: "Φίλτρα",
	toolbarDensity: "Πυκνότητα",
	toolbarExport: "Εξαγωγή",
	columnsPanelTextFieldLabel: "Όνομα στήλης",
	columnsPanelTextFieldPlaceholder: "Τίτλος στήλης",
	columnsPanelDragIconLabel: "Αλλαγή σειράς στηλών",
	columnsPanelShowAllButton: "Εμφάνιση όλων",
	columnsPanelHideAllButton: "Απόκρυψη όλων",
	// Column menu
	columnMenuSortAsc: "Αύξουσα ταξινόμηση",
	columnMenuSortDesc: "Φθίνουσα ταξινόμηση",
	columnMenuFilter: "Φίλτρο",
	columnMenuHideColumn: "Απόκρυψη στήλης",
	columnMenuManageColumns: "Διαχείριση στηλών",
	// Filter panel
	filterPanelAddFilter: "Προσθήκη φίλτρου",
	filterPanelDeleteIconLabel: "Διαγραφή",
	filterPanelLogicOperator: "Λογικός τελεστής",
	filterPanelOperator: "Τελεστής",
	filterPanelColumns: "Στήλες",
	filterPanelInputLabel: "Τιμή",
	filterPanelInputPlaceholder: "Τιμή φίλτρου",
	filterPanelLinkOperator: "Λογικός τελεστής",
	filterPanelOperatorAnd: "Και",
	filterPanelOperatorOr: "Ή",
	filterPanelColumnsLabel: "Στήλη",
	filterPanelSortByLabel: "Ταξινόμηση κατά",
	filterPanelSortAsc: "Αύξουσα",
	filterPanelSortDesc: "Φθίνουσα",
	filterPanelSort: "Ταξινόμηση",
	filterPanelFilterLabel: "Φίλτρα",
	filterPanelFilterOperatorLabel: "Τελεστής",
	filterPanelFilterValueLabel: "Τιμή",
	// Pagination
	MuiTablePagination: {
		labelRowsPerPage: "Γραμμές ανά σελίδα:",
		labelDisplayedRows: ({ from, to, count }) =>
			`${from}–${to} από ${count !== -1 ? count : `περισσότερες από ${to}`}`,
	},
	// Pagination button texts
	firstIconButtonText: "Πρώτη σελίδα",
	previousIconButtonText: "Προηγούμενη σελίδα",
	nextIconButtonText: "Επόμενη σελίδα",
	lastIconButtonText: "Τελευταία σελίδα",
	// No rows
	noRowsLabel: "Δεν υπάρχουν εγγραφές",
	noResultsOverlayLabel: "Δεν βρέθηκαν αποτελέσματα",
	// Filter operators
	filterOperatorContains: "Περιέχει",
	filterOperatorEquals: "Ίσο με",
	filterOperatorStartsWith: "Ξεκινά με",
	filterOperatorEndsWith: "Τελειώνει με",
	filterOperatorIsEmpty: "Είναι κενό",
	filterOperatorIsNotEmpty: "Δεν είναι κενό",
	filterOperatorIsAnyOf: "Είναι ένα από",
	filterOperatorGreaterThan: "Μεγαλύτερο από",
	filterOperatorLessThan: "Μικρότερο από",
	filterOperatorOnOrAfter: "Στις ή μετά",
	filterOperatorOnOrBefore: "Στις ή πριν",
	filterOperatorAfter: "Μετά",
	filterOperatorBefore: "Πριν",
	// Actions
	actionsCellMore: "Περισσότερα",
	densityCompact: "μικρή",
	densityStandard: "κανονική",
	densityComfortable: "άνετη",
};

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/properties`;

const columns = [
	{ field: "aa", headerName: "Α/Α", width: 70, resizable: true },
	{ field: "mitroou", headerName: "Μητρώο", width: 120, editable: true, resizable: true },
	{ field: "kaek", headerName: "KAEK", width: 120, editable: true, resizable: true },
	{ field: "abk", headerName: "ABK", width: 120, editable: true, resizable: true },
	{ field: "x", headerName: "X", width: 90, editable: true, resizable: true },
	{ field: "y", headerName: "Y", width: 90, editable: true, resizable: true },
	{ field: "country", headerName: "Χώρα", width: 120, editable: false, resizable: true },
	{ field: "nomos", headerName: "Νομός", width: 120, editable: false, resizable: true },
	{ field: "dimos", headerName: "Δήμος", width: 120, editable: false, resizable: true },
	{ field: "thesi", headerName: "Θέση", width: 120, editable: true, resizable: true },
	{ field: "topos", headerName: "Τόπος", width: 120, editable: true, resizable: true },
	{ field: "oikismos", headerName: "Οικισμός", width: 120, editable: true, resizable: true },
	{ field: "tk", headerName: "Τκ", width: 80, editable: true, resizable: true, type: "number" },
	{ field: "odos", headerName: "Οδός", width: 230, editable: true, resizable: true },
	{ field: "arithmos", headerName: "Αριθμός", width: 80, editable: true, resizable: true },
	{ field: "kathestos", headerName: "Καθεστώς", width: 120, editable: true, resizable: true },
	{ field: "proelefsi", headerName: "Προέλευση", width: 120, editable: true, resizable: true },
	{ field: "perigrafi", headerName: "Περιγραφή", width: 150, editable: true, resizable: true },
	{
		field: "geofysikos_xaraktiras",
		headerName: "Γεωφυσικός χαρακτήρας",
		width: 160,
		editable: true,
		resizable: true,
	},
	{ field: "trexousa_xrisi", headerName: "Τρέχουσα χρήση", width: 180, editable: true, resizable: true },
	{ field: "alli_xrisi", headerName: "Άλλη χρήση", width: 160, editable: true, resizable: true },
	{ field: "xaraktiras_ktimatos", headerName: "Χαρακτήρας κτήματος", width: 190, editable: true, resizable: true },
	{
		field: "poleodomikos_xaraktiras",
		headerName: "Πολεοδομικός χαρακτήρας",
		width: 190,
		editable: true,
		resizable: true,
	},
	{
		field: "poleodomikes_desmefseis",
		headerName: "Πολεοδομικές δεσμεύσεις",
		width: 160,
		editable: true,
		resizable: true,
	},
	{
		field: "empragmata_dikaiomata",
		headerName: "Εμπράγματα δικαιώματα",
		width: 160,
		editable: true,
		resizable: true,
	},
	{ field: "gipeda", headerName: "Γήπεδα", width: 160, editable: true, resizable: true },
	{
		field: "syndeseis",
		headerName: "Συνδέσεις",
		width: 230,
		editable: true,
		resizable: true,
		renderCell: (params) => {
			if (Array.isArray(params.value)) {
				return params.value.join(", ");
			}
			return params.value ? params.value.split(",").join(", ") : "";
		},
		renderEditCell: (params) => {
			const { id, value, field, api } = params;
			const currentValue = Array.isArray(value) ? value : value ? value.split(",") : [];
			const handleChange = (event) => {
				const newValue = event.target.value;
				api.setEditCellValue({ id, field, value: newValue.join(",") }, event);
			};
			return (
				<Select
					multiple
					value={currentValue}
					onChange={handleChange}
					renderValue={(selected) => selected.join(", ")}
					fullWidth
				>
					{syndeseisOptions.map((option) => (
						<MenuItem key={option} value={option}>
							<Checkbox checked={currentValue.indexOf(option) > -1} />
							<ListItemText primary={option} />
						</MenuItem>
					))}
				</Select>
			);
		},
	},
	{
		field: "emvado_ktimatos",
		headerName: "Εμβαδό κτήματος σε τμ",
		width: 120,
		editable: true,
		resizable: true,
		type: "number",
	},
	{
		field: "pososto_kalypsis",
		headerName: "Ποσοστό κάλυψης (%)",
		width: 120,
		editable: true,
		resizable: true,
		type: "number",
	},
	{ field: "syntelestis_domisis", headerName: "Συντελεστής δόμησης", width: 120, editable: true, resizable: true },
	{ field: "foreas_idioktitis", headerName: "Φορέας ιδιοκτήτης", width: 150, editable: true, resizable: true },
	{ field: "foreas_diaxeirisis", headerName: "Φορέας διαχείρισης", width: 150, editable: true, resizable: true },
	{ field: "xristis_akinitou", headerName: "Χρήστης ακινήτου", width: 150, editable: true, resizable: true },
	{
		field: "antikeimeniki_axia",
		headerName: "Αντικειμενική αξία σε €",
		width: 120,
		editable: true,
		resizable: true,
		type: "number",
	},
	{
		field: "imnia_ypologismou_axias",
		headerName: "Ημ/νία υπολογισμού αξίας",
		width: 120,
		editable: true,
		resizable: true,
		renderCell: (params) => {
			if (!params.value) return "";
			try {
				return dayjs(params.value).format("DD/MM/YYYY");
			} catch (error) {
				return params.value || "";
			}
		},
		renderEditCell: (params) => {
			const { id, value, field, api } = params;

			const handleDateChange = (newValue) => {
				api.setEditCellValue({ id, field, value: newValue });
			};

			return <CustomDatePicker value={value} onChange={handleDateChange} placeholder="DD/MM/YYYY" />;
		},
	},
	{ field: "misthoma", headerName: "Μίσθωμα σε €", width: 120, editable: true, resizable: true, type: "number" },
	{
		field: "imnia_enarxis_misthotiriou",
		headerName: "Ημ/νία έναρξης μισθωτηρίου",
		width: 120,
		editable: true,
		resizable: true,
		renderCell: (params) => {
			if (!params.value) return "";
			try {
				return dayjs(params.value).format("DD/MM/YYYY");
			} catch (error) {
				return params.value || "";
			}
		},
		renderEditCell: (params) => {
			const { id, value, field, api } = params;

			const handleDateChange = (newValue) => {
				api.setEditCellValue({ id, field, value: newValue });
			};

			return <CustomDatePicker value={value} onChange={handleDateChange} placeholder="DD/MM/YYYY" />;
		},
	},
	{ field: "paratiriseis", headerName: "Παρατηρήσεις", width: 150, editable: true, resizable: true },
	{ field: "typos_ktirioy", headerName: "Τύπος κτιρίου", width: 120, editable: true, resizable: true },
	{
		field: "etos_kataskevis",
		headerName: "Έτος κατασκευής",
		width: 120,
		editable: true,
		type: "number",
		resizable: true,
		valueFormatter: (value) => String(value),
	},
	{ field: "orofos", headerName: "Όροφος", width: 120, editable: true, type: "number", resizable: true },
	{
		field: "emvado_ktiriou",
		headerName: "Εμβαδό κτιρίου",
		width: 120,
		editable: true,
		resizable: true,
		type: "number",
	},
];

const defaultRow = {};
columns.forEach((col) => {
	if (col.field !== "actions") {
		// Don't set default values for date fields to preserve null/undefined values
		if (col.type === "date") {
			defaultRow[col.field] = null;
		} else {
			defaultRow[col.field] = "";
		}
	}
});

function normalizeRow(row) {
	return { ...defaultRow, ...row };
}

function CustomDensitySelector({ apiRef }) {
	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);
	const handleOpen = (event) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);
	const setDensity = (density) => {
		if (apiRef?.current) {
			apiRef.current.setDensity(density);
		}
		handleClose();
	};
	return (
		<>
			<Button variant="text" onClick={handleOpen} sx={{ minWidth: 120, textTransform: "none", fontWeight: 500 }}>
				Πυκνότητα
			</Button>
			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				<MenuItem onClick={() => setDensity("compact")} sx={{ textTransform: "none", fontWeight: 500 }}>
					Μικρή
				</MenuItem>
				<MenuItem onClick={() => setDensity("standard")} sx={{ textTransform: "none", fontWeight: 500 }}>
					Κανονική
				</MenuItem>
				<MenuItem onClick={() => setDensity("comfortable")} sx={{ textTransform: "none", fontWeight: 500 }}>
					Άνετη
				</MenuItem>
			</Menu>
		</>
	);
}

function CustomToolbar({
	rows,
	columns,
	apiRef,
	onEditSelected,
	selectedRow,
	onDeleteSelected,
	darkMode,
	onToggleTheme,
}) {
	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);

	const handleMenuOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleExportExcel = () => {
		handleMenuClose();
		const exportData = rows.map((row) => {
			const obj = {};
			columns.forEach((col) => {
				if (col.field !== "actions") obj[col.headerName] = row[col.field];
			});
			return obj;
		});
		const worksheet = XLSX.utils.json_to_sheet(exportData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Δεδομένα");
		XLSX.writeFile(workbook, "data.xlsx");
	};

	const handleExportCSV = () => {
		handleMenuClose();
		if (apiRef?.current?.exportDataAsCsv) {
			apiRef.current.exportDataAsCsv();
		}
	};

	const handleExportPrint = () => {
		handleMenuClose();
		if (apiRef?.current?.exportDataAsPrint) {
			apiRef.current.exportDataAsPrint();
		}
	};

	return (
		<Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
			<GridToolbarColumnsButton sx={{ textTransform: "none", fontWeight: 500, minWidth: 120 }}>
				Στήλες
			</GridToolbarColumnsButton>
			<Button
				variant="text"
				onClick={() => {
					if (apiRef?.current?.showFilterPanel) {
						apiRef.current.showFilterPanel();
					}
				}}
				sx={{
					textTransform: "none",
					fontWeight: 500,
					minWidth: 120,
				}}
			>
				Φίλτρα
			</Button>
			<CustomDensitySelector apiRef={apiRef} />
			<Button
				variant="text"
				onClick={onEditSelected}
				disabled={!selectedRow}
				sx={{ minWidth: 200, textTransform: "none", fontWeight: 500 }}
			>
				Επεξεργασία επιλεγμένης εγγραφής
			</Button>
			<Button
				variant="text"
				color="error"
				startIcon={<DeleteIcon />}
				onClick={onDeleteSelected}
				disabled={!selectedRow}
				sx={{ minWidth: 200, textTransform: "none", fontWeight: 500 }}
			>
				Διαγραφή επιλεγμένης εγγραφής
			</Button>
			<Button
				variant="text"
				startIcon={<DownloadIcon />}
				onClick={handleMenuOpen}
				size="small"
				sx={{ textTransform: "none", fontWeight: 500 }}
			>
				Εξαγωγή
			</Button>
			<Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
				<MenuItem onClick={handleExportCSV} sx={{ textTransform: "none", fontWeight: 500 }}>
					Εξαγωγή σε csv
				</MenuItem>
				<MenuItem onClick={handleExportPrint} sx={{ textTransform: "none", fontWeight: 500 }}>
					Εκτύπωση
				</MenuItem>
				<MenuItem onClick={handleExportExcel} sx={{ textTransform: "none", fontWeight: 500 }}>
					Εξαγωγή σε excel
				</MenuItem>
			</Menu>
			<Box flex={1} />
			<Button
				variant="text"
				startIcon={darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
				onClick={onToggleTheme}
				sx={{ textTransform: "none", fontWeight: 500 }}
			>
				{darkMode ? "Φωτεινό θέμα" : "Σκοτεινό θέμα"}
			</Button>
		</Stack>
	);
}

const PropertiesTable = forwardRef((props, ref) => {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [openForm, setOpenForm] = useState(false);
	const [editRow, setEditRow] = useState(null);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const apiRef = useGridApiRef();
	const [selectedRow, setSelectedRow] = useState(null);
	const [darkMode, setDarkMode] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	// Add allOpen and handleAllOpenChange state/logic at the top level of PropertiesTable
	const [expandedAccordions, setExpandedAccordions] = useState({ basic: false, building: false, property: false });
	const allOpen = Object.values(expandedAccordions).every(Boolean);
	const handleAllOpenChange = () => {
		const newState = !allOpen;
		setExpandedAccordions({ basic: newState, building: newState, property: newState });
	};

	const fetchRows = async () => {
		setLoading(true);
		const token = localStorage.getItem("token") || "";
		try {
			const res = await axios.get(API_URL, { headers: { Authorization: `Bearer ${token}` } });
			setRows(res.data.map(normalizeRow));
		} catch (err) {
			setSnackbar({ open: true, message: "Αποτυχία εξαγωγής δεδομένων", severity: "error" });
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchRows();
	}, []);

	useImperativeHandle(ref, () => ({
		openAddDialog: () => {
			setEditRow(null);
			setOpenForm(true);
		},
	}));

	const dateFields = ["imnia_ypologismou_axias", "imnia_enarxis_misthotiriou"];

	// const handleAdd = () => {
	// 	const today = dayjs().format("YYYY-MM-DD");
	// 	setEditRow({
	// 		...defaultRow,
	// 		syndeseis: [],
	// 		gipeda: "",
	// 		...Object.fromEntries(dateFields.map((field) => [field, today])),
	// 	});
	// 	setOpenForm(true);
	// };

	const handleEdit = async (row) => {
		await fetchRows(); // Ensure latest data from DB
		const refreshedRow = rows.find((r) => r.aa === row.aa) || row;
		let syndeseisValue = refreshedRow.syndeseis;
		if (Array.isArray(syndeseisValue)) {
			// Already an array (e.g., after DataGrid inline edit)
		} else if (typeof syndeseisValue === "string") {
			syndeseisValue = syndeseisValue ? syndeseisValue.split(",") : [];
		} else {
			syndeseisValue = [];
		}
		const setRowToEdit = {
			...refreshedRow,
			syndeseis: syndeseisValue,
			gipeda: refreshedRow.gipeda || "",
			...Object.fromEntries(
				dateFields.map((field) => [
					field,
					refreshedRow[field] ? dayjs(refreshedRow[field]).format("YYYY-MM-DD") : null,
				])
			),
		};
		setEditRow(setRowToEdit);
		setOpenForm(true);
	};

	// Delete
	const handleDelete = async (aa) => {
		if (!window.confirm("Διαγραφή αυτής της εγγραφής;")) return;
		const token = localStorage.getItem("token") || "";
		const URL = `${API_URL}/${aa}`;
		setLoading(true);
		try {
			await axios.delete(URL, { headers: { Authorization: `Bearer ${token}` } });
			setSnackbar({ open: true, message: "Διαγράφηκε επιτυχώς", severity: "success" });
			fetchRows();
		} catch (err) {
			setSnackbar({ open: true, message: `Αποτυχία διαγραφής ${err.response.data.error}`, severity: "error" });
		} finally {
			setLoading(false);
		}
	};

	// Close
	const handleFormClose = () => {
		setOpenForm(false);
		setEditRow(null);
	};

	// Submit via Form
	const handleFormSubmit = async (data) => {
		const submitData = {
			...data,
			syndeseis: Array.isArray(data.syndeseis) ? data.syndeseis.join(",") : data.syndeseis,
			gipeda: data.gipeda || "",
			...Object.fromEntries(
				dateFields.map((field) => [
					field,
					data[field] ? dayjs(data[field]).format("YYYY-MM-DD").toString() : null,
				])
			),
		};
		// console.log("Data to Submit", submitData);
		setLoading(true);
		try {
			let URL;
			const token = localStorage.getItem("token") || "";
			if (editRow) {
				URL = `${API_URL}/${editRow.aa}`;
				await axios.put(URL, submitData, { headers: { Authorization: `Bearer ${token}` } });
				setSnackbar({ open: true, message: "Ενημερώθηκε επιτυχώς", severity: "success" });
			} else {
				URL = API_URL;
				await axios.post(URL, submitData, { headers: { Authorization: `Bearer ${token}` } });
				setSnackbar({ open: true, message: "Προστέθηκε επιτυχώς", severity: "success" });
			}
			fetchRows();
			handleFormClose();
		} catch (err) {
			setSnackbar({ open: true, message: `Αποτυχία αποθήκευσης ${err.response.data.error}`, severity: "error" });
		} finally {
			setLoading(false);
		}
	};

	// Inline edit
	const handleProcessRowUpdate = async (newRow, oldRow) => {
		const updatedRowForBackend = {
			...newRow,
			syndeseis: Array.isArray(newRow.syndeseis) ? newRow.syndeseis.join(",") : newRow.syndeseis,
			imnia_enarxis_misthotiriou: dayjs(newRow.imnia_enarxis_misthotiriou).format("YYYY-MM-DD").toString(),
			imnia_ypologismou_axias: dayjs(newRow.imnia_ypologismou_axias).format("YYYY-MM-DD").toString(),
		};
		if (updatedRowForBackend.nomos !== oldRow.nomos) {
			updatedRowForBackend.dimos = "";
		}
		// console.log("Updating row:", updatedRowForBackend); // Debug log
		setLoading(true);
		try {
			const URL = `${API_URL}/${updatedRowForBackend.aa}`;
			const token = localStorage.getItem("token") || "";
			await axios.put(URL, updatedRowForBackend, { headers: { Authorization: `Bearer ${token}` } });
			setSnackbar({ open: true, message: "Ενημερώθηκε επιτυχώς", severity: "success" });
			fetchRows(); // Force data refresh
			return {
				...newRow,
				syndeseis: Array.isArray(newRow.syndeseis)
					? newRow.syndeseis
					: newRow.syndeseis
					? newRow.syndeseis.split(",")
					: [],
			};
		} catch (err) {
			setSnackbar({ open: true, message: `Αποτυχία ενημέρωσης ${err.response.data.error}`, severity: "error" });
			return oldRow;
		} finally {
			setLoading(false);
		}
	};

	const handleEditSelected = () => {
		if (selectedRow) {
			handleEdit(selectedRow);
		}
	};

	const handleDeleteSelected = () => {
		if (selectedRow) {
			setDeleteDialogOpen(true);
		}
	};

	const handleConfirmDelete = async () => {
		if (!selectedRow) return;
		setDeleteDialogOpen(false);
		setLoading(true);
		try {
			const URL = `${API_URL}/${selectedRow.aa}`;
			const token = localStorage.getItem("token") || "";
			await axios.delete(URL, { headers: { Authorization: `Bearer ${token}` } });
			setSnackbar({ open: true, message: "Διαγράφηκε επιτυχώς", severity: "success" });
			setSelectedRow(null);
			fetchRows();
		} catch (err) {
			setSnackbar({ open: true, message: "Αποτυχία διαγραφής", severity: "error" });
		} finally {
			setLoading(false);
		}
	};

	const actionColumn = {
		field: "actions",
		headerName: "Ενέργειες",
		width: 120,
		sortable: false,
		resizable: true,
		renderCell: (params) => (
			<Stack direction="row" spacing={1}>
				<Tooltip title="Επεξεργασία">
					<span>
						<IconButton size="small" onClick={() => handleEdit(params.row)}>
							<EditIcon />
						</IconButton>
					</span>
				</Tooltip>
				<Tooltip title="Διαγραφή">
					<span>
						<IconButton size="small" color="error" onClick={() => handleDelete(params.row.aa)}>
							<DeleteIcon />
						</IconButton>
					</span>
				</Tooltip>
			</Stack>
		),
	};

	const gridColumns = [...columns.filter((c) => c.field !== "actions"), actionColumn];

	const theme = createTheme({
		palette: {
			mode: darkMode ? "dark" : "light",
		},
		components: {
			MuiButton: {
				styleOverrides: {
					root: {
						// Only affect buttons inside the DataGrid panel
						"&.MuiDataGrid-panel .MuiButton-root, .MuiDataGrid-panel .MuiButton-root": {
							textTransform: "none",
							fontWeight: 500,
						},
					},
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					toolbar: {
						"& .MuiButton-root": {
							textTransform: "none !important",
							fontWeight: 500,
						},
						"& .MuiButton-root span": {
							textTransform: "none !important",
						},
						"& .MuiButton-root *": {
							textTransform: "none !important",
						},
					},
				},
			},
		},
	});

	if (typeof document !== "undefined" && !document.getElementById("datagrid-panel-btn-style")) {
		const style = document.createElement("style");
		style.id = "datagrid-panel-btn-style";
		style.innerHTML = `
			.MuiDataGrid-panel .MuiButton-root {
				text-transform: none !important;
				font-weight: 500 !important;
			}
			.MuiDataGrid-toolbar .MuiButton-root {
				text-transform: none !important;
				font-weight: 500 !important;
			}
			.MuiDataGrid-toolbarContainer .MuiButton-root {
				text-transform: none !important;
				font-weight: 500 !important;
			}
			.MuiDataGrid-toolbar .MuiButton-root span {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbarContainer .MuiButton-root span {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbar .MuiButton-root .MuiButton-startIcon + span {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-startIcon + span {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbar .MuiButton-root .MuiButton-endIcon + span {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-endIcon + span {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbar .MuiButton-root .MuiButton-label {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-label {
				text-transform: none !important;
			}
			/* Target all button text content */
			.MuiDataGrid-toolbar .MuiButton-root * {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbarContainer .MuiButton-root * {
				text-transform: none !important;
			}
			/* Specific targeting for filter button */
			.MuiDataGrid-toolbar .MuiButton-root:has(span:contains("Φίλτρα")) {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbar .MuiButton-root:has(span:contains("Φίλτρα")) * {
				text-transform: none !important;
			}
			/* Specific targeting for columns button */
			.MuiDataGrid-toolbar .MuiButton-root:has(span:contains("Στήλες")) {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbar .MuiButton-root:has(span:contains("Στήλες")) * {
				text-transform: none !important;
			}
			/* Target GridToolbarColumnsButton specifically */
			.MuiDataGrid-toolbar .MuiButton-root[data-testid="GridToolbarColumnsButton"] {
				text-transform: none !important;
			}
			.MuiDataGrid-toolbar .MuiButton-root[data-testid="GridToolbarColumnsButton"] * {
				text-transform: none !important;
			}
		`;
		document.head.appendChild(style);
	}

	return (
		<ThemeProvider theme={theme}>
			<>
				<Box
					sx={{
						width: "100vw",
						flex: 1,
						display: "flex",
						flexDirection: "column",
						maxHeight: "70vh",

						boxSizing: "border-box",
					}}
				>
					<div
						style={{
							flex: 1,
							height: "100%",
							width: "100%",
							background: darkMode ? "#222" : "#fff",
							margin: 0,
							padding: 0,
							display: "flex",
							flexDirection: "column",
							minHeight: 0,
						}}
					>
						<DataGrid
							rows={rows}
							columns={gridColumns}
							loading={loading}
							columnResizeMode="onChange"
							disableColumnMenu={false}
							sx={{
								flex: 1,
								height: "100%",
								"& .MuiDataGrid-panel .MuiButton-root": {
									textTransform: "none",
									fontWeight: 500,
								},
								"& .MuiDataGrid-toolbar .MuiButton-root": {
									textTransform: "none",
									fontWeight: 500,
								},
								"& .MuiDataGrid-toolbarContainer .MuiButton-root": {
									textTransform: "none",
									fontWeight: 500,
								},
								"& .MuiDataGrid-toolbar .MuiButton-root span": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbarContainer .MuiButton-root span": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbar .MuiButton-root .MuiButton-startIcon + span": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-startIcon + span": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbar .MuiButton-root .MuiButton-endIcon + span": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-endIcon + span": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbar .MuiButton-root .MuiButton-label": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-label": {
									textTransform: "none",
								},
								/* Specific targeting for columns button */
								"& .MuiDataGrid-toolbar .MuiButton-root[data-testid='GridToolbarColumnsButton']": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbar .MuiButton-root[data-testid='GridToolbarColumnsButton'] *": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbar .MuiButton-root:has(span:contains('Στήλες'))": {
									textTransform: "none",
								},
								"& .MuiDataGrid-toolbar .MuiButton-root:has(span:contains('Στήλες')) *": {
									textTransform: "none",
								},
								"& .MuiDataGrid-columnHeaders": {
									cursor: "col-resize",
								},
								"& .MuiDataGrid-columnHeader": {
									cursor: "col-resize",
								},
								"& .MuiDataGrid-columnSeparator": {
									cursor: "col-resize",
								},
								"& .MuiDataGrid-columnHeaderTitleContainer": {
									cursor: "col-resize",
								},
							}}
							slots={{
								toolbar: CustomToolbar,
							}}
							slotProps={{
								toolbar: {
									rows,
									columns: gridColumns,
									apiRef,
									onEditSelected: handleEditSelected,
									selectedRow,
									onDeleteSelected: handleDeleteSelected,
									darkMode,
									onToggleTheme: () => setDarkMode((d) => !d),
								},
							}}
							localeText={localeText}
							pageSize={10}
							rowsPerPageOptions={[10, 25, 50]}
							disableSelectionOnClick
							getRowId={(row) => row.aa}
							apiRef={apiRef}
							processRowUpdate={handleProcessRowUpdate}
							onRowSelectionModelChange={(ids) => {
								const id = Array.isArray(ids) && ids.length > 0 ? ids[0] : null;
								const row = rows.find((r) => r.aa === id);
								setSelectedRow(row || null);
							}}
						/>
					</div>
				</Box>
				<Dialog
					open={openForm}
					onClose={handleFormClose}
					maxWidth={false}
					fullWidth
					sx={{ "& .MuiDialog-paper": { width: "95vw", height: "95vh" } }}
				>
					<DialogTitle>
						<Box display="flex" alignItems="center" justifyContent="space-between">
							<Box display="flex" alignItems="center">
								<IconButton
									edge="start"
									color="inherit"
									onClick={handleFormClose}
									aria-label="close"
									sx={{ mr: 2 }}
								>
									<CloseIcon />
								</IconButton>
								<Typography variant="h6">
									{editRow ? "Επεξεργασία εγγραφής" : "Προσθήκη εγγραφής"}
								</Typography>
							</Box>
							<Box display="flex" alignItems="center">
								<Typography variant="body2" sx={{ mr: 1 }}>
									{allOpen ? "Κλείσιμο όλων" : "Άνοιγμα όλων"}
								</Typography>
								<Switch checked={allOpen} onChange={handleAllOpenChange} />
							</Box>
						</Box>
					</DialogTitle>
					<Box sx={{ mb: "10px" }} />
					<Divider />
					<DialogContent>
						<PropertyForm
							initialData={editRow}
							onSubmit={handleFormSubmit}
							onCancel={handleFormClose}
							allOpen={allOpen}
							handleAllOpenChange={handleAllOpenChange}
							expandedAccordions={expandedAccordions}
							setExpandedAccordions={setExpandedAccordions}
						/>
					</DialogContent>
				</Dialog>
				<Snackbar
					open={snackbar.open}
					autoHideDuration={3000}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
				>
					<Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
						{snackbar.message}
					</Alert>
				</Snackbar>
				<Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
					<DialogTitle>Διαγραφή εγγραφής</DialogTitle>
					<DialogContent>Είστε σίγουροι ότι θέλετε να διαγράψετε την επιλεγμένη εγγραφή;</DialogContent>
					<DialogActions>
						<Button onClick={() => setDeleteDialogOpen(false)}>Ακύρωση</Button>
						<Button onClick={handleConfirmDelete} color="error" variant="contained">
							Διαγραφή
						</Button>
					</DialogActions>
				</Dialog>
			</>
		</ThemeProvider>
	);
});

export default PropertiesTable;
